import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { TenantProvider, GLOBAL_PATHS, useTenant } from '@/context/TenantContext';
import { FilterProvider } from '@/context/FilterContext';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Campagnes from '@/pages/Campagnes';
import CampagneDetail from '@/pages/CampagneDetail';
import Operations from '@/pages/Operations';
import Performances from '@/pages/Performances';
import Analyses from '@/pages/Analyses';
import Comparaison from '@/pages/Comparaison';
import Objectifs from '@/pages/Objectifs';
import Consolidation from '@/pages/Consolidation';
import Comparatif from '@/pages/Comparatif';
import Territoires from '@/pages/Territoires';
import Carte from '@/pages/Carte';
import Responsables from '@/pages/Responsables';
import Recruteurs from '@/pages/Recruteurs';
import Historique from '@/pages/Historique';
import Exports from '@/pages/Exports';
import Parametres from '@/pages/Parametres';
import Admin from '@/pages/Admin';
import SuiviSocial from '@/pages/SuiviSocial';
import PlanStrategique from '@/pages/PlanStrategique';
import Attrition from '@/pages/Attrition';
import PilotageREX from '@/pages/PilotageREX';

const FullLoader = () => (
  <div className="app-bg flex min-h-screen items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-[color:var(--tenant-accent-500)]" />
      <span className="font-display text-sm text-muted-foreground">Chargement de SHARITI…</span>
    </div>
  </div>
);

// The consolidated view ("Toutes les associations") is admin-only.
// Consolidated mode is derived from the route itself (see TenantContext), so the
// guard only needs to keep non-admins out — no state juggling, no redirect loops.
function TenantRouteGuard() {
  const { isAdmin, isCampaignManager } = useAuth();
  const { isAll } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Campaign managers may access their scoped "Recruteurs" view; every other
    // global/admin path stays admin-only.
    const cmAllowed = isCampaignManager && location.pathname === '/recruteurs';
    if (GLOBAL_PATHS.includes(location.pathname) && !isAdmin && !cmAllowed) {
      navigate('/', { replace: true });
    } else if (isAll && location.pathname === '/') {
      // In aggregated mode, the consolidated view is the home dashboard.
      navigate('/consolidation', { replace: true });
    }
  }, [isAdmin, isCampaignManager, isAll, location.pathname, navigate]);
  return null;
}

function ProtectedApp() {
  const { user, loading, isAdmin, isCampaignManager } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // Route-level RBAC: internal / admin & global pages must never render for
  // association (or campaign-manager) users, even via a direct URL.
  const adminOnly = (el) => (isAdmin ? el : <Navigate to="/" replace />);
  const teamView = (el) => (isAdmin || isCampaignManager ? el : <Navigate to="/" replace />);
  // Suivi Social : réservé au propriétaire OU admin explicitement autorisé (opt-in).
  const socialOnly = (el) => (isAdmin && (user?.is_owner === true || user?.can_view_social === true) ? el : <Navigate to="/" replace />);
  // Plan stratégique / Attrition : réservé au propriétaire OU admin explicitement autorisé (opt-in).
  const strategyOnly = (el) => (isAdmin && (user?.is_owner === true || user?.can_view_strategy === true) ? el : <Navigate to="/" replace />);
  // Pilotage REX : STRICTEMENT réservé au propriétaire (non délégable).
  const ownerOnly = (el) => (isAdmin && user?.is_owner === true ? el : <Navigate to="/" replace />);
  return (
    <TenantProvider>
      <FilterProvider>
        <TenantRouteGuard />
        <AppShell>
          <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/campagnes" element={<Campagnes />} />
            <Route path="/campagnes/:code" element={<CampagneDetail />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/performances" element={<Performances />} />
            <Route path="/analyses" element={<Analyses />} />
            <Route path="/comparaison" element={<Comparaison />} />
            <Route path="/objectifs" element={<Objectifs />} />
            <Route path="/consolidation" element={adminOnly(<Consolidation />)} />
            <Route path="/comparatif" element={adminOnly(<Comparatif />)} />
            <Route path="/territoires" element={adminOnly(<Territoires />)} />
            <Route path="/carte" element={adminOnly(<Carte />)} />
            <Route path="/responsables" element={adminOnly(<Responsables />)} />
            <Route path="/recruteurs" element={teamView(<Recruteurs />)} />
            <Route path="/historique" element={adminOnly(<Historique />)} />
            <Route path="/exports" element={<Exports />} />
            <Route path="/parametres" element={<Parametres />} />
            <Route path="/admin" element={adminOnly(<Admin />)} />
            <Route path="/suivi-social" element={socialOnly(<SuiviSocial />)} />
            <Route path="/plan-strategique" element={strategyOnly(<PlanStrategique />)} />
            <Route path="/attrition" element={strategyOnly(<Attrition />)} />
            <Route path="/pilotage-rex" element={ownerOnly(<PilotageREX />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </AppShell>
      </FilterProvider>
    </TenantProvider>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  // Handle Google OAuth callback (session_id in URL fragment) before anything else.
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={loading ? <FullLoader /> : (user ? <Navigate to="/" replace /> : <Login />)} />
      <Route path="/*" element={<ProtectedApp />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
